import { useParams } from 'react-router-dom';
import { useCaseStudy, useAuthorDiscussions } from '../hooks/useApi';
import { useState } from 'react';

function CaseStudy() {
  const { author } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const { data: caseStudyData, isLoading: isCaseStudyLoading, error: caseStudyError } = useCaseStudy(author);
  const {
    data: discussionsData,
    isLoading: isDiscussionsLoading
  } = useAuthorDiscussions(author, currentPage, pageSize);

  if (isCaseStudyLoading || isDiscussionsLoading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  if (caseStudyError) {
    return <div className="text-red-500">Error loading case study: {caseStudyError.message}</div>;
  }

  if (!caseStudyData?.[0]) {
    return <div className="text-red-500">Case study not found</div>;
  }

  const renderPagination = () => {
    if (!discussionsData || discussionsData.totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(discussionsData.totalPages, startPage + maxVisiblePages - 1);

    // Adjust startPage if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add first page button
    if (startPage > 1) {
      pages.push(
        <button
          key="first"
          onClick={() => setCurrentPage(1)}
          className="px-3 py-1 rounded border hover:bg-gray-50"
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis1" className="px-2">...</span>);
      }
    }

    // Add page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 rounded border ${
            currentPage === i
              ? 'bg-blue-600 text-white'
              : 'hover:bg-gray-50'
          }`}
        >
          {i}
        </button>
      );
    }

    // Add last page button
    if (endPage < discussionsData.totalPages) {
      if (endPage < discussionsData.totalPages - 1) {
        pages.push(<span key="ellipsis2" className="px-2">...</span>);
      }
      pages.push(
        <button
          key="last"
          onClick={() => setCurrentPage(discussionsData.totalPages)}
          className="px-3 py-1 rounded border hover:bg-gray-50"
        >
          {discussionsData.totalPages}
        </button>
      );
    }

    return (
      <div className="flex items-center justify-center space-x-2 mt-4">
        <button
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
          className={`px-3 py-1 rounded border ${
            currentPage === 1
              ? 'text-gray-400 cursor-not-allowed'
              : 'hover:bg-gray-50'
          }`}
        >
          Previous
        </button>
        {pages}
        <button
          onClick={() => setCurrentPage(prev => Math.min(discussionsData.totalPages, prev + 1))}
          disabled={currentPage === discussionsData.totalPages}
          className={`px-3 py-1 rounded border ${
            currentPage === discussionsData.totalPages
              ? 'text-gray-400 cursor-not-allowed'
              : 'hover:bg-gray-50'
          }`}
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <div className="prose max-w-none markdown-content">
        <div
          className="prose max-w-none"
          dangerouslySetInnerHTML={{ __html: caseStudyData[0].case_study_html }}
        />
      </div>

      {discussionsData?.discussions && discussionsData.discussions.length > 0 && (
        <div className="mt-8">
          <h2 className="text-2xl font-bold mb-4">
            Author Discussions
            <span className="text-sm font-normal text-gray-600 ml-2">
              ({discussionsData.totalCount} total)
            </span>
          </h2>
          <div className="space-y-4">
            {discussionsData.discussions.map((discussion) => (
              <div key={discussion.source_id} className="border rounded-lg p-4 hover:bg-gray-50">
                <a
                  href={discussion.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <h3 className="text-lg font-semibold text-blue-600 hover:underline">{discussion.title}</h3>
                  <div className="mt-2 text-sm text-gray-600">
                    <span className="font-medium">Source: </span>{discussion.source_type}
                    <span className="mx-2">•</span>
                    <span className="font-medium">Posted: </span>
                    {new Date(discussion.published_at).toLocaleDateString()}
                  </div>
                  {discussion.summary && (
                    <p className="mt-2 text-gray-700">{discussion.summary}</p>
                  )}
                  <div className="mt-2 text-sm">
                    <span className="font-medium">Relevance Score: </span>
                    <span className="text-green-600">{discussion.relevance_score.toFixed(2)}</span>
                  </div>
                </a>
              </div>
            ))}
          </div>
          {renderPagination()}
        </div>
      )}
    </div>
  );
}

export default CaseStudy;